import { useUser } from '@folklore/auth';
import {
    useQuery,
    keepPreviousData as previousPlaceholder,
    useQueryClient,
    useMutation,
} from '@tanstack/react-query';

import { useApi } from '../contexts/ApiContext';
import { updateAuthUser } from './useAuth';

export function useSubmissions(query = null, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { id: userId = null } = useUser() || {};
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['submissions', query, userId],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.submissions.get({ paginated: false, ...queryParam }, null, null, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    // const { data: items, pagination } = data || {};

    return data !== null
        ? {
              items: data,
              //   pagination,
              ...queryResult,
          }
        : queryResult;
}

export function useSubmission(id, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { id: userId = null } = useUser() || {};
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['submission', id, userId],
        queryFn: ({ queryKey: [, idParam], signal }) =>
            api.submissions.findById(idParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    // const { data: items, pagination } = data || {};

    return data !== null
        ? {
              submission: data,
              //   pagination,
              ...queryResult,
          }
        : queryResult;
}

export function useDocumentSubmission(documentId, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { id: userId = null } = useUser() || {};
    const { data = null, ...queryResult } = useQuery<Submission>({
        queryKey: ['submission_document', documentId, userId],
        queryFn: ({ queryKey: [, idParam], signal }) =>
            api.submissions.findByDocument(idParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    // const { data: items, pagination } = data || {};

    return data !== null
        ? {
              submission: data,
              //   pagination,
              ...queryResult,
          }
        : queryResult;
}

export function useUrlSubmission(url: string, opts = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { id: userId = null } = useUser() || {};
    const { data = null, ...queryResult } = useQuery<Submission>({
        queryKey: ['submission_url', url, userId],
        queryFn: ({ queryKey: [, urlParam], signal }) =>
            api.submissions.findByUrl(urlParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    // const { data: items, pagination } = data || {};

    return data !== null
        ? {
              submission: data,
              //   pagination,
              ...queryResult,
          }
        : queryResult;
}

export function useFormSubmission(formId: string, opts = null) {
    const api = useApi();
    const { keepPreviousData = true, url = null } = opts || {};
    const { id: userId = null } = useUser() || {};
    const { data = null, ...queryResult } = useQuery<FormSubmission>({
        queryKey: ['submission_form', formId, userId],
        queryFn: ({ queryKey: [, idParam], signal }) =>
            api.submissions.findByForm(idParam, url !== null ? { url } : null, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        enabled: formId !== null,
        ...opts,
    });

    // const { data: items, pagination } = data || {};

    return {
        submission: data,
        //   pagination,
        ...queryResult,
    };
}

interface SubmissionMutationOptions {
    form?: string | null;
    onSuccess?: (data: SubmissionMutationResponse) => void;
}

function updateFromSubmissionResponse(queryClient, response: SubmissionMutationResponse) {
    const { user: newUser, submission: newSubmission } = response;
    const { id: newUserId = null } = newUser || {};
    const { id, document = null, form = null } = newSubmission || {};
    const { id: documentId = null } = document || {};
    const { id: formId = null } = form || {};
    updateAuthUser(queryClient, newUser);
    if (newSubmission !== null) {
        queryClient.setQueryData(['submission', id, newUserId], newSubmission);
    }
    if (documentId !== null) {
        queryClient.setQueryData(['submission_document', documentId, newUserId], newSubmission);
    }
    if (formId !== null) {
        queryClient.setQueryData(['submission_form', formId, newUserId], newSubmission);
    }
}

export function useAnswerFormSubmission(formId: string, opts?: SubmissionMutationOptions) {
    const { onSuccess = null, ...otherOpts } = opts || {};
    const queryClient = useQueryClient();
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        FormSubmissionMutationResponse,
        Error,
        FormSubmissionAnswerRequest
    >({
        mutationFn: (data) => api.submissions.answerForm(formId, data),
        onSuccess: (newData) => {
            updateFromSubmissionResponse(queryClient, newData);

            if (onSuccess !== null) {
                onSuccess(newData);
            }
        },
        ...otherOpts,
    });

    return {
        answer: mutate,
        answerAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useSubmitFormSubmission(formId: string, opts?: SubmissionMutationOptions) {
    return useMutateSubmission<FormSubmissionMutationResponse, FormSubmissionSubmitRequest>({
        form: formId,
        ...opts,
    });
}

export function useMutateSubmission<
    TData extends SubmissionMutationResponse = SubmissionMutationResponse,
    TVariables = void,
>(opts?: SubmissionMutationOptions) {
    const { onSuccess = null, form: formId = null, ...otherOpts } = opts || {};
    const queryClient = useQueryClient();
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<TData, Error, TVariables>({
        mutationFn: (data) =>
            formId !== null
                ? api.submissions.submitForm(formId, data)
                : api.submissions.submit(data),
        onSuccess: (newData) => {
            updateFromSubmissionResponse(queryClient, newData);

            if (onSuccess !== null) {
                onSuccess(newData);
            }
        },
        ...otherOpts,
    });

    return {
        submit: mutate,
        submitAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useIdentifyFormSubmission(formId: string, opts?: SubmissionMutationOptions) {
    return useIdentifySubmission<FormSubmissionMutationResponse>({
        form: formId,
        ...opts,
    });
}

export function useIdentifySubmission<
    TData extends SubmissionMutationResponse = SubmissionMutationResponse,
>(opts?: SubmissionMutationOptions) {
    const { onSuccess = null, form: formId = null, ...otherOpts } = opts || {};
    const queryClient = useQueryClient();
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<TData>({
        mutationFn: (data) =>
            formId !== null
                ? api.submissions.identifyForm(formId, data)
                : api.submissions.identify(data),
        onSuccess: (newData) => {
            updateFromSubmissionResponse(queryClient, newData);

            if (onSuccess !== null) {
                onSuccess(newData);
            }
        },
        ...otherOpts,
    });

    return {
        identify: mutate,
        identifyAsync: mutateAsync,
        ...mutationResult,
    };
}
